// client/src/components/marketer/MarketerProjectInfluencerSelect.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  readProjectList, 
  cacheProjectList,
  readApplications,
  cacheApplications,
  clearMarketerCache
} from '../../utils/marketerProjectCache';
import ProjectDetailModal from './project/modals/ProjectDetailModal';
import SelectContractModal from './project/modals/SelectContractModal';
import CompleteModal from './project/modals/CompleteModal';

// ✅ 이미지 프리로드 함수 (브라우저 캐시 활용)
const preloadImage = (url) => {
  if (!url) return;
  const img = new Image();
  img.src = url;
};

function MarketerProjectInfluencerSelect() {
  const location = useLocation();
  
  // ✅ uid를 상태로 관리 (이벤트 기반 업데이트)
  const [uid, setUid] = useState(window.__ME__?.id);
  
  // ✅✅✅ 컴포넌트 마운트 시 항상 최신 window.__ME__ 확인
  useEffect(() => {
    // ✅ 마운트 시 즉시 동기화 (이전 사용자 상태 제거)
    const currentUid = window.__ME__?.id;
    if (currentUid !== uid) {
      setUid(currentUid);
    }
    
    const handleUserReady = (event) => {
      const userId = event.detail?.id;
      if (userId) {
        setUid(userId);
      }
    };
    
    // ✅ 글로벌 리스너 배열에 등록 (StrictMode cleanup에 안전!)
    if (!window.__USER_READY_LISTENERS__) {
      window.__USER_READY_LISTENERS__ = [];
    }
    window.__USER_READY_LISTENERS__.push(handleUserReady);
    
    // cleanup
    return () => {
      // ✅ 배열에서 제거
      if (window.__USER_READY_LISTENERS__) {
        const idx = window.__USER_READY_LISTENERS__.indexOf(handleUserReady);
        if (idx > -1) {
          window.__USER_READY_LISTENERS__.splice(idx, 1);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ 의존성 배열 비움! (최초 1회만 실행, uid는 의도적으로 제외)
  
  // ✅ uid가 변경될 때마다 캐시 재계산 (useMemo)
  const initialProjects = useMemo(() => {
    return uid ? (readProjectList(uid) || []) : [];
  }, [uid]);
  
  const hasProjectCache = uid && initialProjects.length > 0;
  
  // ✅ initialApplications도 useMemo로 최적화
  const initialApplications = useMemo(() => {
    if (!hasProjectCache || !initialProjects[0]?.id) return [];
    return readApplications(initialProjects[0].id, 'all') || [];
  }, [hasProjectCache, initialProjects]);
  
  const hasAppCache = initialApplications.length > 0;
  
  // ✅ 상태 초기화 (캐시 기반)
  const [projects, setProjects] = useState(initialProjects);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [applications, setApplications] = useState(initialApplications);
  const [loading, setLoading] = useState(!hasProjectCache); // ✅ 캐시 있으면 false
  const [applicationsLoading, setApplicationsLoading] = useState(!hasAppCache && hasProjectCache); // ✅ 프로젝트는 있지만 지원자가 없을 때만 true
  const [error, setError] = useState(null);
  const [filterPendingOnly, setFilterPendingOnly] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [hiddenApplicationIds, setHiddenApplicationIds] = useState(new Set());
  const [isInitialLoad, setIsInitialLoad] = useState(!hasProjectCache); // ✅✅ 캐시가 있으면 false로 시작!
  
  // ✅ 선정 모달 상태
  const [selectModal, setSelectModal] = useState({
    open: false,
    applicationId: null,
    influencerName: '',
    needsTrackingUrl: false
  });
  
  // ✅ 완료 모달 상태
  const [completeModal, setCompleteModal] = useState(false);
  
  // ✅ 새로고침 상태
  const [refreshing, setRefreshing] = useState(false);

  // ✅✅ 지원자 목록 백그라운드 프리페치 함수 (UI 업데이트 없음)
  const prefetchApplications = useCallback(async (projectId) => {
    if (!projectId) return;
    
    const filter = filterPendingOnly ? 'pending' : 'all';
    
    // ✅ 이미 캐시에 있으면 스킵
    const cached = readApplications(projectId, filter);
    if (cached) {
      return; // 이미 있음
    }
    
    // ✅ 백그라운드에서 조용히 로드 (로딩 상태 업데이트 안 함!)
    try {
      const res = await fetch(
        `/api/marketer/projects/${projectId}/applications?filter=${filter}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      
      if (res.ok && data.ok && data.applications) {
        // ✅ 캐시에만 저장, UI는 업데이트 안 함
        cacheApplications(projectId, data.applications, filter);
        console.log(`✅ 프로젝트 ${projectId} 지원자 프리페치 완료 (${data.applications.length}명)`);
      }
    } catch (e) {
      console.warn(`지원자 프리페치 실패 (${projectId}):`, e);
      // ✅ 실패해도 조용히 무시 (사용자 경험에 영향 없음)
    }
  }, [filterPendingOnly]);

  // 특정 프로젝트의 지원자 목록 조회
  const fetchApplications = useCallback(async (projectId, force = false) => {
    const filter = filterPendingOnly ? 'pending' : 'all';
    
    // ✅ 캐시 우선 확인 (force가 false일 때만)
    if (!force) {
      const cached = readApplications(projectId, filter);
      if (cached) {
        setApplications(cached);
        setApplicationsLoading(false);
        return;
      }
    }

    setApplicationsLoading(true);
    setHiddenApplicationIds(new Set());
    try {
      const res = await fetch(
        `/api/marketer/projects/${projectId}/applications?filter=${filter}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      
      if (!res.ok || !data.ok) {
        throw new Error(data.message || '지원자 목록 조회 실패');
      }
      
      setApplications(data.applications || []);
      // ✅ 캐시 저장 (필터 포함)
      cacheApplications(projectId, data.applications, filter);
    } catch (e) {
      console.error('지원자 목록 조회 오류:', e);
      setApplications([]);
    } finally {
      setApplicationsLoading(false);
    }
  }, [filterPendingOnly]);

  // 프로젝트 목록 조회
  const fetchProjects = useCallback(async (force = false) => {
    // 캐시 우선 확인 (force가 false일 때만)
    if (!force && uid) {
      const cached = readProjectList(uid);
      if (cached && cached.length > 0) {
        setProjects(cached);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/marketer/projects/with-applications', {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (!res.ok || !data.ok) {
        throw new Error(data.message || '프로젝트 목록 조회 실패');
      }
      
      const fetchedProjects = data.projects || [];
      setProjects(fetchedProjects);
      if (uid) {
        cacheProjectList(uid, fetchedProjects);
      }
      
      // ✅ 프로젝트 로드 후 첫 2개 이미지 백그라운드 프리로드
      if (fetchedProjects.length > 0) {
        setTimeout(() => {
          if (fetchedProjects[0]?.coverImage) {
            preloadImage(fetchedProjects[0].coverImage);
          }
          if (fetchedProjects[1]?.coverImage) {
            preloadImage(fetchedProjects[1].coverImage);
          }
        }, 0);
      }
      
    } catch (e) {
      console.error('프로젝트 목록 조회 오류:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  // ✅ uid가 변경되면 캐시에서 상태 업데이트
  useEffect(() => {
    if (uid && initialProjects.length > 0) {
      // ✅ 캐시가 있는 경우
      setProjects(initialProjects);
      setLoading(false);
      if (initialApplications.length > 0) {
        setApplications(initialApplications);
        setApplicationsLoading(false);
      }
      setIsInitialLoad(false);
    } else if (uid && initialProjects.length === 0) {
      // ✅✅✅ uid는 있지만 캐시가 없는 경우
      setLoading(true);
      setIsInitialLoad(true);
    }
  }, [uid, initialProjects, initialApplications]);

  // ✅ 초기 로드 + 이미지 프리로드 + 지원자 프리페치
  useEffect(() => {
    if (!isInitialLoad) {
      if (projects.length > 0) {
        if (projects[0]?.coverImage) {
          preloadImage(projects[0].coverImage);
        }
        if (projects[1]?.coverImage) {
          preloadImage(projects[1].coverImage);
        }
        
        if (projects[1]?.id) {
          setTimeout(() => {
            prefetchApplications(projects[1].id);
          }, 100);
        }
      }
      return;
    }
    
    const loadProjects = async () => {
      await fetchProjects();
      setIsInitialLoad(false);
    };
    loadProjects();
  }, [fetchProjects, isInitialLoad, projects, prefetchApplications]);

  // ✅ 필터 변경 또는 프로젝트 변경 시 지원자 재조회
  useEffect(() => {
    if (!isInitialLoad && projects.length > 0 && currentProjectIndex < projects.length) {
      fetchApplications(projects[currentProjectIndex].id);
    }
  }, [filterPendingOnly, currentProjectIndex, projects, fetchApplications, isInitialLoad]);

  // ✅ URL 파라미터에서 projectId 읽어서 해당 프로젝트로 자동 이동
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetProjectId = params.get('projectId');
    
    if (targetProjectId && projects.length > 0 && !isInitialLoad) {
      const targetIndex = projects.findIndex(p => p.id === targetProjectId);
      if (targetIndex !== -1 && targetIndex !== currentProjectIndex) {
        setCurrentProjectIndex(targetIndex);
        const newParams = new URLSearchParams(location.search);
        newParams.delete('projectId');
        window.history.replaceState({}, '', `${location.pathname}?${newParams.toString()}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, location.search, isInitialLoad]);

  const currentProject = projects[currentProjectIndex];

  // ✅ 디버깅: 현재 프로젝트 정보 확인
  useEffect(() => {
    if (currentProject) {
      console.log('[DEBUG] Current Project:', {
        id: currentProject.id,
        projectNumber: currentProject.projectNumber,
        serviceType: currentProject.serviceType,
        productName: currentProject.productName,
        hasProductName: !!currentProject.productName
      });
    }
  }, [currentProject]);

  // ✅ 프로젝트 이동 + 이미지 프리로드 + 지원자 프리페치
  const handlePrevProject = () => {
    if (currentProjectIndex > 0) {
      const newIndex = currentProjectIndex - 1;
      const nextProjectId = projects[newIndex]?.id;
      
      if (nextProjectId) {
        const filter = filterPendingOnly ? 'pending' : 'all';
        const cached = readApplications(nextProjectId, filter);
        if (cached) {
          setApplications(cached);
        }
      }
      
      setCurrentProjectIndex(newIndex);
      
      if (newIndex > 0 && projects[newIndex - 1]?.coverImage) {
        preloadImage(projects[newIndex - 1].coverImage);
      }
      
      if (newIndex > 0 && projects[newIndex - 1]?.id) {
        setTimeout(() => {
          prefetchApplications(projects[newIndex - 1].id);
        }, 0);
      }
    }
  };

  const handleNextProject = () => {
    if (currentProjectIndex < projects.length - 1) {
      const newIndex = currentProjectIndex + 1;
      const nextProjectId = projects[newIndex]?.id;
      
      if (nextProjectId) {
        const filter = filterPendingOnly ? 'pending' : 'all';
        const cached = readApplications(nextProjectId, filter);
        if (cached) {
          setApplications(cached);
        }
      }
      
      setCurrentProjectIndex(newIndex);
      
      if (newIndex < projects.length - 1 && projects[newIndex + 1]?.coverImage) {
        preloadImage(projects[newIndex + 1].coverImage);
      }
      
      if (newIndex < projects.length - 1 && projects[newIndex + 1]?.id) {
        setTimeout(() => {
          prefetchApplications(projects[newIndex + 1].id);
        }, 0);
      }
    }
  };

  const handleMoveToBottom = (applicationId) => {
    setHiddenApplicationIds(prev => new Set([...prev, applicationId]));
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const year = String(d.getFullYear()).slice(2);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  const sortedApplications = useMemo(() => {
    return [...applications].sort((a, b) => {
      const aHidden = hiddenApplicationIds.has(a.id);
      const bHidden = hiddenApplicationIds.has(b.id);
      if (aHidden && !bHidden) return 1;
      if (!aHidden && bHidden) return -1;
      return 0;
    });
  }, [applications, hiddenApplicationIds]);

  const getStatusText = useCallback((app) => {
    if (app.status === 'pending') {
      return null; 
    }
    if (app.status === 'selected') {
      if (app.contractStatus === 'pending') {
        return '계약요청중입니다.(수락 대기중)';
      }
      if (app.contractStatus === 'rejected') {
        return '계약을 거절하였습니다.(지원 취소)';
      }
      if (app.contractStatus === 'accepted') {
        return '계약 완료되었습니다.';
      }
      return '선정(계약요청 보냈기)';
    }
    if (app.status === 'expired') {
      return '계약 만료되었습니다.(선정 만료)';
    }
    return '-';
  }, []);

  const getSNSIcon = useCallback((platform) => {
    const iconMap = {
      instagram: '/images/sns/instagram.svg',
      youtube: '/images/sns/youtube.svg',
      blog: '/images/sns/blog.svg',
      tiktok: '/images/sns/tiktok.svg'
    };
    
    const iconPath = iconMap[platform] || '/images/sns/default.svg';
    
    return (
      <img 
        src={iconPath}
        alt={platform}
        style={{
          width: '20px',
          height: '20px',
          objectFit: 'contain',
          verticalAlign: 'middle'
        }}
      />
    );
  }, []);

  const formatFollowers = useCallback((count) => {
    if (count >= 10000) {
      return `${Math.floor(count / 10000)}만`;
    }
    return count.toLocaleString();
  }, []);

  const handleSelectClick = useCallback((app) => {
    const needsTrackingUrl = currentProject?.perPurchaseReward?.use === true;
    
    setSelectModal({
      open: true,
      applicationId: app.id,
      influencerName: app.influencer?.name || '인플루언서',
      needsTrackingUrl
    });
  }, [currentProject]);

  const executeSelection = useCallback(async (data) => {
    const { trackingUrl, marketerRequest } = data;
    const applicationId = selectModal.applicationId;
    
    setSelectModal({ ...selectModal, open: false });
    setCompleteModal(true);
    
    setApplications(prev => {
      const updated = prev.map(app => {
        if (app.id === applicationId) {
          return { 
            ...app, 
            status: 'selected', 
            contractStatus: 'pending',
            selectedAt: new Date().toISOString()
          };
        }
        return app;
      });
      
      cacheApplications(currentProject.id, updated, 'all');
      cacheApplications(currentProject.id, updated, 'pending');
      
      return updated;
    });
    
    setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/marketer/projects/${currentProject.id}/select`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              applicationId,
              trackingUrl,
              marketerRequest
            })
          }
        );
        
        const result = await res.json();
        
        if (!res.ok || !result.ok) {
          // ✅ 모든 에러에서 UI 롤백 (pending 상태로 복구)
          setApplications(prev => {
            const rolledBack = prev.map(app => 
              app.id === applicationId 
                ? { ...app, status: 'pending', contractStatus: 'none', selectedAt: null }
                : app
            );
            
            // ✅ ALREADY_PROCESSED가 아닌 경우만 캐시에 저장
            if (result.message !== 'ALREADY_PROCESSED') {
              cacheApplications(currentProject.id, rolledBack, 'all');
              cacheApplications(currentProject.id, rolledBack, 'pending');
            }
            
            return rolledBack;
          });
          
          // ✅ ALREADY_PROCESSED 에러 시 캐시 무효화 + 재조회로 최신 데이터 반영
          if (result.message === 'ALREADY_PROCESSED') {
            clearMarketerCache(uid);
            await fetchApplications(currentProject.id);
            await fetchProjects();
          }
          
          const errorMessages = {
            'HEADCOUNT_EXCEEDED': '모집 인원이 이미 가득 찼습니다.',
            'ALREADY_PROCESSED': '인플루언서가 방금 지원을 취소해서, 선정이 불가능합니다.',
            'RECRUITMENT_ENDED': '모집 기간이 종료되었습니다.',
            'APPLICATION_NOT_FOUND': '지원서를 찾을 수 없습니다.'
          };
          
          alert(errorMessages[result.message] || '선정에 실패했습니다.');
          return;
        }
        
        await fetchApplications(currentProject.id);
        await fetchProjects();
        
      } catch (e) {
        console.error('선정 오류:', e);
        
        setApplications(prev => {
          const rolledBack = prev.map(app => 
            app.id === applicationId 
              ? { ...app, status: 'pending', contractStatus: 'none', selectedAt: null }
              : app
          );
          
          cacheApplications(currentProject.id, rolledBack, 'all');
          cacheApplications(currentProject.id, rolledBack, 'pending');
          
          return rolledBack;
        });
        
        alert('네트워크 오류가 발생했습니다.');
      }
    }, 0);
  }, [selectModal, currentProject, fetchApplications, fetchProjects, uid]);

  // ✅ 새로고침 함수
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchProjects(true); // force=true
      if (currentProject) {
        await fetchApplications(currentProject.id, true); // force=true
      }
    } catch (e) {
      console.error('새로고침 오류:', e);
    } finally {
      setRefreshing(false);
    }
  }, [fetchProjects, fetchApplications, currentProject]);

  if (!uid) {
    return (
      <div className="mb-card mb-card--full-height" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--mb-text-sub)' }}>
          <div style={{ fontSize: 16 }}>초기화 중...</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mb-card mb-card--full-height" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--mb-text-sub)' }}>
          <div style={{ fontSize: 16, marginBottom: 8 }}>프로젝트 목록을 불러오는 중...</div>
          <div style={{ fontSize: 13 }}>잠시만 기다려주세요</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-card mb-card--full-height" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#e5484d', marginBottom: 16, fontSize: 16, fontWeight: 600 }}>
            오류: {error}
          </div>
        <button onClick={fetchProjects} className="mb-btn mb-btn-secondary">
          다시 시도
        </button>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="mb-card mb-card--full-height" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: 'var(--mb-text-title)' }}>
            선정 가능한 프로젝트가 없습니다
          </div>
          <div style={{ fontSize: 14, color: 'var(--mb-text-sub)' }}>
          모집 마감일로부터 3일 이내의 프로젝트만 표시됩니다.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-card mb-card--full-height">
      {/* 상단 안내 */}
      <div className="mb-page-header">
        <h2 className="mb-page-title">인플루언서 선정하기</h2>
        <p className="mb-page-description">프로젝트에 지원한 인플루언서를 확인하고 계약 요청을 보내주세요.</p>
      </div>

      {/* 프로젝트 네비게이션 카드 */}
      <div className="pnav">
        <div className="pnav__top">
          <div className="pnav__pager">
            <button
              onClick={handlePrevProject}
              disabled={currentProjectIndex === 0}
              aria-label="이전 프로젝트"
            >
              ‹
            </button>
            <span>{currentProjectIndex + 1} / {projects.length}</span>
            <button
              onClick={handleNextProject}
              disabled={currentProjectIndex === projects.length - 1}
              aria-label="다음 프로젝트"
            >
              ›
            </button>
          </div>
          <div className="pnav__spacer" />
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="mb-btn mb-btn-secondary mb-btn-sm"
          >
            {refreshing ? '불러오는 중...' : '새로고침'}
          </button>
          <button
            onClick={() => setShowDetailModal(true)}
            className="mb-btn mb-btn-secondary mb-btn-sm"
          >
            프로젝트 상세보기
          </button>
        </div>

        <div className="pnav__no">PROJECT NO.{currentProject?.projectNumber || 'N/A'}</div>
        {currentProject?.productName && (
          <div className="pnav__title">
            {currentProject.productName}
            을(를) 홍보하는{' '}
            {currentProject.serviceType === 'delivery' && '제품 제공'}
            {currentProject.serviceType === 'purchase' && '제품 미제공'}
            {currentProject.serviceType === 'visit' && '장소 방문'}
            {currentProject.serviceType === 'free' && '자율'}
            {!['delivery', 'purchase', 'visit', 'free'].includes(currentProject.serviceType) && '일반'}
            {' '}프로젝트입니다
          </div>
        )}

        <div className="pnav__stats">
          <div className="pnav__stat">
            <b>{currentProject?.acceptedCount || 0}</b>
            <span>계약 완료</span>
          </div>
          <div className="pnav__stat">
            <b>{currentProject?.pendingContractCount || 0}</b>
            <span>수락 대기</span>
          </div>
          <div className="pnav__stat">
            <b>{currentProject?.headcount || 0}</b>
            <span>모집 인원</span>
          </div>
          <div className="pnav__stat">
            <b>{currentProject?.appliedCount || 0}</b>
            <span>총 지원자</span>
          </div>
        </div>

        <div className="pnav__dates">
          <span>
            모집 기간{' '}
            <b>
              {currentProject?.createdAt && formatDateShort(currentProject.createdAt)}
              {' ~ '}
              {currentProject?.recruitEnd && formatDateShort(currentProject.recruitEnd)}
            </b>
          </span>
        </div>

        {currentProject?.recruitEnd && (
          <div className="note note--blue">
            인플루언서 선정은 <b>모집 마감일({new Date(currentProject.recruitEnd).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' }).replace(/\. /g, '.').replace(/\.$/, '')})</b>까지 가능하며,{' '}
            프로젝트는 <b>모집 마감 후 3일({new Date(new Date(currentProject.recruitEnd).getTime() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' }).replace(/\. /g, '.').replace(/\.$/, '')})</b>까지 이 목록에 표시됩니다.
          </div>
        )}
      </div>

      {/* 필터 */}
      <div className="chips">
        <button
          type="button"
          className={`chip${!filterPendingOnly ? ' is-active' : ''}`}
          onClick={() => setFilterPendingOnly(false)}
        >
          전체 지원자
        </button>
        <button
          type="button"
          className={`chip${filterPendingOnly ? ' is-active' : ''}`}
          onClick={() => setFilterPendingOnly(true)}
        >
          미선정 상태만 보기
        </button>
      </div>

      {/* 지원자 테이블 */}
      {applicationsLoading ? (
        <div style={{ 
          textAlign: 'center', 
          padding: 60, 
          color: 'var(--mb-text-sub)',
          background: 'white',
          borderRadius: 'var(--mb-card-radius)',
          border: '1px solid var(--mb-border-color)'
        }}>
          <div style={{ fontSize: 15 }}>지원자 목록을 불러오는 중...</div>
        </div>
      ) : sortedApplications.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: 60, 
          color: 'var(--mb-text-sub)',
          background: 'white',
          borderRadius: 'var(--mb-card-radius)',
          border: '1px solid var(--mb-border-color)'
        }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: 'var(--mb-text-body)' }}>
            지원자가 없습니다
          </div>
          <div style={{ fontSize: 14 }}>
            아직 이 프로젝트에 지원한 인플루언서가 없습니다.
          </div>
        </div>
      ) : (
        <div>
          {sortedApplications.map((app, idx) => (
            <ApplicationRow
              key={app.id}
              app={app}
              index={idx + 1}
              onMoveToBottom={handleMoveToBottom}
              isHidden={hiddenApplicationIds.has(app.id)}
              getSNSIcon={getSNSIcon}
              formatFollowers={formatFollowers}
              getStatusText={getStatusText}
              currentProject={currentProject}
              onSelect={() => handleSelectClick(app)}
            />
          ))}
        </div>
      )}

      {/* 프로젝트 상세 모달 */}
      <ProjectDetailModal
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        project={currentProject}
      />

      {/* ✅ 선정 모달 */}
      <SelectContractModal
        open={selectModal.open}
        onClose={() => setSelectModal({ ...selectModal, open: false })}
        influencerName={selectModal.influencerName}
        needsTrackingUrl={selectModal.needsTrackingUrl}
        onConfirm={executeSelection}
      />

      {/* ✅ 완료 모달 */}
      <CompleteModal
        open={completeModal}
        onClose={() => setCompleteModal(false)}
      />
    </div>
  );
}

// ✅ 지원자 카드 컴포넌트 (A안) - React.memo로 최적화
const ApplicationRow = React.memo(({
  app,
  index,
  onMoveToBottom,
  isHidden,
  getSNSIcon,
  formatFollowers,
  getStatusText,
  currentProject,
  onSelect
}) => {
  const statusText = getStatusText(app);
  const isPending = app.status === 'pending';
  const isSelectionFull = (currentProject?.selectedCount || 0) >= (currentProject?.headcount || 0);

  const isRecruitmentEnded = currentProject?.recruitEnd
    ? new Date(currentProject.recruitEnd) < new Date()
    : false;

  const isButtonDisabled = isSelectionFull || isRecruitmentEnded;

  const totalFollowers = (app.snsAccounts || []).reduce((sum, s) => sum + (Number(s.followerCount) || 0), 0);
  const name = app.influencer?.name || '-';

  return (
    <div
      className="mb-card"
      style={{
        marginBottom: 12,
        ...(isHidden ? { opacity: 0.55 } : {}),
        ...(app.status === 'selected' ? { boxShadow: '0 0 0 2px var(--mb-color-primary), var(--mb-shadow-card)' } : {}),
      }}
    >
      {/* 헤드: 아바타 + 이름 + 상태 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' }}>
        <span
          aria-hidden="true"
          style={{
            width: 40, height: 40, borderRadius: 12, background: '#eef3ff', color: '#1f4fd6',
            fontWeight: 800, fontSize: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          {name.charAt(0)}
        </span>
        <div style={{ flex: 1, minWidth: 140 }}>
          <div className="t-name" style={{ fontSize: 15 }}>{name} <span className="t-sub" style={{ display: 'inline', marginLeft: 2 }}>#{index}</span></div>
          {app.influencer?.userAddress && <div className="t-sub">{app.influencer.userAddress}</div>}
        </div>
        {!isPending && (
          <span className={`mb-badge ${app.status === 'selected' ? 'mb-badge-blue' : 'mb-badge-gray'}`}>
            {statusText}
          </span>
        )}
      </div>

      {/* 지원 SNS 채널 (전체 나열) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: 12 }}>
        {(app.snsAccounts || []).map((sns, i) => (
          <a key={i} href={sns.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <span className="platform">
              {getSNSIcon(sns.platform)}
              @{String(sns.accountName || '').replace(/^@/, '')} · {formatFollowers(sns.followerCount)}명
            </span>
          </a>
        ))}
      </div>

      {/* A안: 스탯 스트립 */}
      <div className="mbh-astats">
        <div><b>{formatFollowers(totalFollowers)}</b><span>총 팔로워</span></div>
        <div>
          <b>{app.influencer?.satisfactionScore > 0 ? `★ ${app.influencer.satisfactionScore}` : '—'}</b>
          <span>{app.influencer?.satisfactionScore > 0 ? '만족도 (5점 만점)' : '평가 없음'}</span>
        </div>
        <div>
          <b>{app.influencer?.totalSelections || 0}회</b>
          <span>선정 · 취소 {app.influencer?.contractCancelCount || 0}회</span>
        </div>
      </div>

      {/* 풋: 선정/보류 */}
      {isPending && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {isRecruitmentEnded && (
            <div style={{ color: '#e5484d', fontSize: 12.5, fontWeight: 600 }}>모집 기간 종료</div>
          )}
          {!isRecruitmentEnded && isSelectionFull && (
            <div style={{ color: '#9a6b00', fontSize: 12.5, fontWeight: 600 }}>모집 인원 마감</div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onSelect}
              disabled={isButtonDisabled}
              className="mb-btn mb-btn-primary"
              style={{ flex: 1 }}
              title={
                isRecruitmentEnded
                  ? '모집 기간이 종료되었습니다'
                  : isSelectionFull
                    ? '모집 인원이 가득 찼습니다'
                    : '선정하기'
              }
            >
              이 인플루언서 선정하기 (계약 요청)
            </button>
            <button
              onClick={() => onMoveToBottom(app.id)}
              className="mb-btn mb-btn-secondary"
            >
              아래로
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default MarketerProjectInfluencerSelect;
